import React, { useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { 
    Box, 
    Button, 
    CircularProgress, 
    Typography,
    Card,
    Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FournisseurTable from "../components/FournisseurTable";
import ProjectModal from '../components/ProjectModal';
import { getFournisseurs } from '../services/projectService';
import { Fournisseur } from "../interfaces/Fournisseur";

const FournisseursManagement: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFournisseur, setSelectedFournisseur] = useState<Fournisseur | null>(null);
    const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchFournisseurs = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getFournisseurs();
            setFournisseurs(response.data as Fournisseur[]);
        } catch (error) {
            setError("Erreur lors de la récupération des fournisseurs");
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFournisseurs();
    }, []);

    const handleCreateFournisseur = () => {
        setSelectedFournisseur(null);
        setIsModalOpen(true);
    };

    const handleEditFournisseur = (fournisseur: Fournisseur) => {
        setSelectedFournisseur(fournisseur);
        setIsModalOpen(true);
    };

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                                Gestion des fournisseurs
                            </Typography>
                            <Typography variant="body1" className="text-emerald-600">
                                Gérez vos fournisseurs et leurs informations
                            </Typography>
                        </div>
                        <Button
                            variant="contained"
                            onClick={handleCreateFournisseur}
                            startIcon={<AddIcon />}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Ajouter un fournisseur
                        </Button>
                    </Box>
                </Card>

                {/* Messages d'erreur */}
                {error && (
                    <Alert 
                        severity="error" 
                        className="rounded-lg border border-red-100"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Contenu principal */}
                <Card className="bg-white/80 backdrop-blur-sm overflow-hidden">
                    {loading ? (
                        <Box className="flex justify-center items-center p-12">
                            <CircularProgress className="text-emerald-600" />
                        </Box>
                    ) : (
                        <Box className="overflow-x-auto">
                            <FournisseurTable
                                fournisseur={fournisseurs}
                                onEditFournisseur={handleEditFournisseur}
                                refreshFournisseurs={fetchFournisseurs}
                            />
                        </Box>
                    )}
                </Card>

                {/* Modal de création/édition */}
                <ProjectModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={() => {
                        setIsModalOpen(false);
                        fetchFournisseurs();
                    }}
                    project={selectedFournisseur}
                />
            </Box>
        </MainLayout>
    );
};

export default FournisseursManagement;